import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
  type Signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CheckPoint } from '../../core/models/checkPoint.model';
import { Form } from '../../core/models/form.model';
import { AssetInstanceService } from '../../core/services/asset-instance.service';
import { BreadcrumbService } from '../../core/services/breadcrumb.service';
import { CheckPointService } from '../../core/services/checkPoint.service';
import { FormService } from '../../core/services/form.service';
import { LoggerService } from '../../core/services/logger.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { CardContentComponent } from '../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../shared/components/card/card-title.component';
import { CardComponent } from '../../shared/components/card/card.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { TranslatePipe } from '../../shared/pipes/translate.pipe';

/**
 * Component for recording formSubmission with an asset
 * Shows available forms or auto-routes if only one type is configured
 * Uses Reactive Query Signal (RQS) pattern for automatic data updates
 */
@Component({
  selector: 'app-choose-form-to-submit',
  imports: [
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    SpinnerComponent,
    ButtonComponent,
    IconComponent,
    TranslatePipe,
  ],
  templateUrl: './choose-form-to-submit.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './choose-form-to-submit.component.css',
})
export class ChooseFormToSubmit implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private checkPointService = inject(CheckPointService);
  private formService = inject(FormService);
  private assetInstanceService = inject(AssetInstanceService);
  private breadcrumbService = inject(BreadcrumbService);

  // RQS pattern signals - set in ngOnInit
  checkPoint!: Signal<CheckPoint | null>;
  checkPointLoading!: Signal<boolean>;
  checkPointError!: Signal<string | null>;

  forms!: Signal<Form[]>;
  formsLoading!: Signal<boolean>;
  formsError!: Signal<string | null>;

  // Combined loading/error states
  loading = computed(() => this.checkPointLoading?.() || this.formsLoading?.() || false);
  error = computed(() => {
    const checkPointErr = this.checkPointError?.();
    const typesErr = this.formsError?.();
    return checkPointErr || typesErr || null;
  });

  // Manual error for missing asset ID
  initError = signal<string | null>(null);
  private logger = inject(LoggerService).getLogger('choose-form-to-submit');

  // Query params for view-mode navigation (admin "View" from instance row)
  private mode: string | null = null;
  private instanceId: string | null = null;

  constructor() {
    // Auto-route when data loads if only one interaction type
    effect(() => {
      const types = this.forms?.();
      const checkPoint = this.checkPoint?.();
      const isLoading = this.loading();

      if (checkPoint) {
        this.breadcrumbService.setDynamicLabel(checkPoint.name);
      }

      if (!isLoading && types && checkPoint && types.length === 1) {
        this.logger.debug('[RecordInteraction] Auto-routing to single interaction type');
        // Replace this page in history: Back from the form (or from its success
        // page) must not land here and auto-route straight back into the form --
        // for an auto_submit form that would record a second submission.
        void this.selectForm(types[0], { replaceUrl: true });
      }
    });
  }

  async ngOnInit() {
    // Get checkpoint ID from route
    const checkPointId = this.route.snapshot.paramMap.get('checkpointId');
    this.logger.debug('[RecordInteraction] Checkpoint ID from route:', checkPointId);

    // Read optional query params for view-mode (used by admin instance row "View" button)
    this.mode = this.route.snapshot.queryParamMap.get('mode');
    this.instanceId = this.route.snapshot.queryParamMap.get('instanceId');

    if (!checkPointId) {
      this.initError.set('No checkpoint ID provided');
      return;
    }

    // Create reactive queries using RQS pattern
    const checkPointQuery = this.checkPointService.createQueryById(checkPointId);
    this.checkPoint = checkPointQuery.data;
    this.checkPointLoading = checkPointQuery.loading;
    this.checkPointError = checkPointQuery.error;

    const typesQuery = this.formService.createQueryByCheckPointId(checkPointId);
    this.forms = typesQuery.data;
    this.formsLoading = typesQuery.loading;
    this.formsError = typesQuery.error;
  }

  async selectForm(interactionType: Form, options: { replaceUrl?: boolean } = {}) {
    const checkPoint = this.checkPoint();
    if (!checkPoint) return;
    const extras = options.replaceUrl ? [{ replaceUrl: true }] : [];

    if (this.mode === 'view') {
      // View-mode: navigate to the form view screen (optionally instance-scoped)
      if (this.instanceId) {
        void this.router.navigate(
          ['/checkpoints', checkPoint.id, interactionType.id, this.instanceId, 'view'],
          ...extras,
        );
      } else {
        void this.router.navigate(
          ['/checkpoints', checkPoint.id, interactionType.id, 'view'],
          ...extras,
        );
      }
      return;
    }

    // Submit mode: check if checkpoint has instances to route through choose-instance step
    const hasInstances = await this.assetInstanceService.hasInstances(checkPoint.id!);
    if (hasInstances) {
      void this.router.navigate(
        ['/submission', checkPoint.id, interactionType.id, 'choose-instance'],
        ...extras,
      );
    } else {
      void this.router.navigate(['/submission', checkPoint.id, interactionType.id], ...extras);
    }
  }

  viewCheckPoint() {
    const checkPoint = this.checkPoint();
    if (checkPoint) {
      this.router.navigate(['/checkpoints', checkPoint.id]);
    }
  }

  backToScan() {
    this.router.navigate(['/scan']);
  }
}
